import { BookOpenCheck, Files, FolderSearch } from 'lucide-react';
import { santoroTestimonial } from '@/content/testimonials';
import type { SolutionContent } from './types';

export const avvocatiSolution: SolutionContent = {
  slug: 'avvocati',
  navLabel: 'Avvocati',
  navDescription: 'Atti, quesiti procedurali e giurisprudenza, dal fascicolo al deposito.',
  meta: {
    title: 'AI per avvocati',
    description:
      'Ricostruisci il quadro normativo e giurisprudenziale, analizza atti e fascicoli e arriva a una prima bozza. Ogni riferimento si apre sul documento originale.',
  },
  hero: {
    title: 'Trovanorma per avvocati',
    subtitle:
      'Al tuo fianco quando arriva un fascicolo da ricostruire, una questione procedurale da verificare, una memoria da preparare o un atto di controparte da analizzare.',
  },
  showcaseAudience: 'avvocati',
  queryExamples: [
    {
      title: 'Ricorso per decreto ingiuntivo',
      icon: 'BadgeEuro',
      question:
        'Ti allego tre fatture non pagate e i DDT firmati: redigi il ricorso per decreto ingiuntivo ex art. 633 c.p.c., con istanza di provvisoria esecuzione.',
      attachment: 'fatture-e-ddt.pdf',
      result: 'Bozza di atto in Word',
    },
    {
      title: 'Atto di controparte',
      icon: 'FileSearch',
      question:
        'Allego la comparsa di controparte: smonta le sue eccezioni una per una e dimmi dove la mia memoria è attaccabile.',
      attachment: 'comparsa-controparte.pdf',
      result: 'Analisi con giurisprudenza',
    },
    {
      title: 'Sfratto per morosità',
      icon: 'KeyRound',
      question:
        'Il conduttore non paga da **tre mesi**: redigi intimazione di sfratto e citazione per la convalida partendo dal contratto allegato.',
      attachment: 'contratto-locazione.pdf',
      result: 'Bozza di atto in Word',
    },
    {
      title: 'Richiesta danni da sinistro',
      icon: 'CarFront',
      question:
        'Prepara la richiesta di risarcimento ex art. 144 Codice delle Assicurazioni per il mio assistito: dinamica e documenti nel fascicolo allegato.',
      attachment: 'fascicolo-sinistro.pdf',
      result: 'Lettera pronta da inviare',
    },
    {
      title: 'Mediazione o giudizio?',
      icon: 'Handshake',
      question:
        'Lite condominiale per infiltrazioni: la mediazione è obbligatoria o posso citare direttamente? E con quali tempi realistici?',
      result: 'Risposta con le fonti',
    },
    {
      title: 'Pignoramento presso terzi',
      icon: 'Landmark',
      question:
        'Ho il decreto ingiuntivo esecutivo: prepara l’atto di pignoramento presso terzi ex art. 543 c.p.c. verso la banca del debitore.',
      result: 'Bozza di atto in Word',
    },
  ],
  tasks: [
    {
      icon: BookOpenCheck,
      title: 'Quesiti giuridici e procedurali',
      description:
        'Ricostruisci il quadro applicabile su termini, rito, competenza, notifiche, prescrizione e questioni normative e verifica direttamente i riferimenti utilizzati.',
    },
    {
      icon: FolderSearch,
      title: 'Fascicolo e atti da analizzare',
      description:
        'Porta gli allegati del caso o un atto ricevuto e approfondisci fatti, riferimenti, orientamenti giurisprudenziali e punti che meritano una verifica.',
    },
    {
      icon: Files,
      title: 'Redazione e revisione',
      description:
        'Dalla memoria al ricorso, dalla diffida al contratto: costruisci una prima bozza, continua a lavorarci e confronta le versioni fino al testo definitivo.',
    },
  ],
  comparison: {
    title: 'Non tutte le AI sono costruite per il lavoro legale',
    text: 'Quando una risposta entra in un parere o in un atto, deve poter essere verificata e utilizzata.',
    genericLabel: 'AI generalista',
    drawbacks: [
      'Riferimenti a norme e sentenze non sempre presenti',
      'Aggiornamento delle fonti non garantito',
      'Normativa e giurisprudenza trattate in un contesto generalista',
      'Testi da adattare alla struttura di atti e pareri',
      'Chat separata dai documenti e da Word',
    ],
    strengths: [
      'Riferimenti collegati ai documenti originali',
      'Fonti ufficiali sincronizzate ogni giorno',
      'Normativa, pronunce e orientamenti messi in relazione',
      'Pareri, atti e revisioni già strutturati',
      'Dalla ricerca al documento e a Word',
    ],
  },
  featuresTitle: 'Le funzionalità più utili nel lavoro legale',
  featuredFeatures: [
    {
      slug: 'ricerca-e-quesiti',
      description:
        'Ricostruisci il quadro normativo e giurisprudenziale e verifica direttamente le fonti.',
    },
    {
      slug: 'ricerca-nelle-fonti',
      description:
        'Vai dritto alla sentenza o all’articolo, per estremi o per parole chiave, e leggine subito la sintesi.',
    },
    {
      slug: 'analisi-documentale',
      description:
        'Analizza fascicoli, atti e allegati nel contesto della questione su cui stai lavorando.',
    },
    {
      slug: 'redazione-documenti',
      description:
        'Dal caso e dagli allegati a una prima bozza strutturata da rivedere e completare.',
    },
  ],
  cta: {
    title: 'La prova migliore è il tuo prossimo fascicolo',
    text: 'Porta un caso reale, un atto o un quesito su cui stai già lavorando e verifica come Trovanorma può supportarti dalla ricerca alla prima bozza.',
  },
  testimonial: santoroTestimonial,
};
